import * as THREE from 'three';
import type { BodyDef } from '../data/bodies';
import { moonAuFromParent } from '../data/bodies';
import { auToScene, radiusKmToScene } from '../scale/units';
import { makeRingTexture } from '../assets/textures';

export class CelestialBody {
  def: BodyDef;
  group: THREE.Group;
  mesh: THREE.Mesh;
  atmosphere?: THREE.Mesh;
  clouds?: THREE.Mesh;
  rings?: THREE.Mesh;
  radius: number;
  collisionRadius: number;
  worldPosition = new THREE.Vector3();
  /** Orbital radius in scene units (from parent or sun) */
  orbitRadiusScene: number;
  parent?: CelestialBody;
  children: CelestialBody[] = [];
  private orbitAngle: number;
  private spin = 0;

  constructor(
    def: BodyDef,
    textures: {
      map?: THREE.Texture;
      clouds?: THREE.Texture;
    },
  ) {
    this.def = def;
    this.group = new THREE.Group();
    this.group.name = def.id;
    this.radius = radiusKmToScene(def.radiusKm);
    // Minimum visible size for tiny moons
    if (this.radius < 0.02 && def.kind === 'moon') {
      this.radius = Math.max(this.radius, 0.035);
    }

    this.orbitAngle = def.phase;
    this.orbitRadiusScene = 0;

    const segs = def.kind === 'star' ? 64 : def.radiusKm > 10_000 ? 64 : 48;
    const geo = new THREE.SphereGeometry(this.radius, segs, segs);

    let mat: THREE.Material;
    if (def.kind === 'star') {
      mat = new THREE.MeshBasicMaterial({
        map: textures.map ?? null,
        color: textures.map ? 0xffffff : new THREE.Color(def.color),
      });
    } else {
      mat = new THREE.MeshStandardMaterial({
        map: textures.map ?? null,
        color: textures.map ? 0xffffff : new THREE.Color(def.color),
        roughness: def.hasAtmosphere ? 0.75 : 0.9,
        metalness: 0.05,
        emissive: new THREE.Color(def.color).multiplyScalar(0.02),
      });
    }

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = true;
    this.group.add(this.mesh);

    if (def.tilt) {
      this.mesh.rotation.z = THREE.MathUtils.degToRad(def.tilt);
    }

    // Clouds layer (Earth)
    if (textures.clouds) {
      const cloudGeo = new THREE.SphereGeometry(this.radius * 1.008, segs, segs);
      const cloudMat = new THREE.MeshStandardMaterial({
        map: textures.clouds,
        transparent: true,
        opacity: 0.45,
        depthWrite: false,
        roughness: 1,
      });
      this.clouds = new THREE.Mesh(cloudGeo, cloudMat);
      this.group.add(this.clouds);
    }

    // Atmosphere Fresnel shell
    if (def.hasAtmosphere) {
      const scale = def.atmosphereScale ?? 1.03;
      const atmGeo = new THREE.SphereGeometry(this.radius * scale, segs, segs);
      const atmMat = new THREE.ShaderMaterial({
        uniforms: {
          color: { value: new THREE.Color(def.atmosphereColor ?? '#88bbff') },
          coefficient: { value: 0.5 },
          power: { value: 3.5 },
        },
        vertexShader: /* glsl */ `
          varying vec3 vNormal;
          varying vec3 vView;
          void main() {
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            vNormal = normalize(normalMatrix * normal);
            vView = normalize(-mv.xyz);
            gl_Position = projectionMatrix * mv;
          }
        `,
        fragmentShader: /* glsl */ `
          uniform vec3 color;
          uniform float coefficient;
          uniform float power;
          varying vec3 vNormal;
          varying vec3 vView;
          void main() {
            float fresnel = coefficient + (1.0 - coefficient) * pow(1.0 - abs(dot(vView, vNormal)), power);
            gl_FragColor = vec4(color, fresnel * 0.55);
          }
        `,
        transparent: true,
        depthWrite: false,
        side: THREE.FrontSide,
        blending: THREE.AdditiveBlending,
      });
      this.atmosphere = new THREE.Mesh(atmGeo, atmMat);
      this.group.add(this.atmosphere);
    }

    // Rings
    if (def.hasRings) {
      const inner = this.radius * (def.ringInner ?? 1.2);
      const outer = this.radius * (def.ringOuter ?? 2.2);
      const ringGeo = new THREE.RingGeometry(inner, outer, 128);
      // UV fix for ring texture
      const pos = ringGeo.attributes.position;
      const uv = ringGeo.attributes.uv;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const len = Math.sqrt(x * x + y * y);
        uv.setXY(i, (len - inner) / (outer - inner), 0.5);
      }
      const ringTex = makeRingTexture(def.ringColor ?? '#c9b896');
      const ringMat = new THREE.MeshBasicMaterial({
        map: ringTex,
        side: THREE.DoubleSide,
        transparent: true,
        depthWrite: false,
        opacity: 0.9,
      });
      this.rings = new THREE.Mesh(ringGeo, ringMat);
      this.rings.rotation.x = Math.PI / 2;
      if (def.tilt) {
        this.rings.rotation.x = Math.PI / 2;
        this.group.rotation.z = THREE.MathUtils.degToRad(def.tilt * 0.35);
      }
      this.group.add(this.rings);
    }

    // Sun glow
    if (def.kind === 'star') {
      const glowGeo = new THREE.SphereGeometry(this.radius * 1.35, 32, 32);
      const glowMat = new THREE.ShaderMaterial({
        uniforms: {
          color: { value: new THREE.Color('#ffaa44') },
        },
        vertexShader: /* glsl */ `
          varying vec3 vNormal;
          varying vec3 vView;
          void main() {
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            vNormal = normalize(normalMatrix * normal);
            vView = normalize(-mv.xyz);
            gl_Position = projectionMatrix * mv;
          }
        `,
        fragmentShader: /* glsl */ `
          uniform vec3 color;
          varying vec3 vNormal;
          varying vec3 vView;
          void main() {
            float f = pow(0.65 - dot(vView, vNormal), 2.0);
            gl_FragColor = vec4(color, clamp(f, 0.0, 0.6));
          }
        `,
        transparent: true,
        depthWrite: false,
        side: THREE.BackSide,
        blending: THREE.AdditiveBlending,
      });
      this.group.add(new THREE.Mesh(glowGeo, glowMat));

      const corona = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: makeSoftGlow(),
          color: 0xffcc66,
          transparent: true,
          opacity: 0.55,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      corona.scale.setScalar(this.radius * 6);
      this.group.add(corona);
    }

    this.collisionRadius =
      this.radius * (def.hasAtmosphere ? (def.atmosphereScale ?? 1.03) : 1.02);
    if (def.hasRings && def.ringOuter) {
      // Don't use ring outer as hard collision — only body
    }
  }

  setParent(parent: CelestialBody): void {
    this.parent = parent;
    parent.children.push(this);
    const orbitKm = this.def.orbitKm ?? 0;
    // Moon orbits: scale up slightly so systems are readable near planets
    const moonScale = 18;
    this.orbitRadiusScene = auToScene(moonAuFromParent(orbitKm)) * moonScale;
    // Ensure moons aren't inside parent
    this.orbitRadiusScene = Math.max(
      this.orbitRadiusScene,
      parent.radius * 2.5 + this.radius * 3,
    );
  }

  setHeliocentricOrbit(): void {
    this.orbitRadiusScene = auToScene(this.def.semiMajorAu);
  }

  update(dt: number, timeScale = 1): void {
    // Orbital motion
    if (this.def.periodDays !== 0) {
      const omega = ((Math.PI * 2) / Math.abs(this.def.periodDays)) * timeScale;
      const dir = this.def.periodDays < 0 ? -1 : 1;
      this.orbitAngle += omega * dt * dir;
    }

    const parentPos = this.parent
      ? this.parent.worldPosition
      : new THREE.Vector3(0, 0, 0);

    if (this.def.id !== 'sun') {
      const inc = THREE.MathUtils.degToRad(this.def.inclination);
      const e = this.def.eccentricity;
      const a = this.orbitRadiusScene;
      // Simple elliptical approximation in orbital plane
      const r = (a * (1 - e * e)) / (1 + e * Math.cos(this.orbitAngle));
      const x = Math.cos(this.orbitAngle) * r;
      const z = Math.sin(this.orbitAngle) * r;
      const y = Math.sin(this.orbitAngle) * Math.sin(inc) * r * 0.35;
      this.group.position.set(parentPos.x + x, parentPos.y + y, parentPos.z + z);
    } else {
      this.group.position.set(0, 0, 0);
    }

    this.group.getWorldPosition(this.worldPosition);

    // Spin
    if (this.def.rotationHours) {
      const spinSpeed = ((Math.PI * 2) / Math.abs(this.def.rotationHours)) * timeScale * 0.15;
      this.spin += spinSpeed * dt * Math.sign(this.def.rotationHours || 1);
      this.mesh.rotation.y = this.spin;
      if (this.clouds) this.clouds.rotation.y = this.spin * 1.05;
    }
  }
}

function makeSoftGlow(): THREE.CanvasTexture {
  const s = 128;
  const c = document.createElement('canvas');
  c.width = s;
  c.height = s;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  g.addColorStop(0, 'rgba(255,230,150,1)');
  g.addColorStop(0.3, 'rgba(255,180,60,0.5)');
  g.addColorStop(1, 'rgba(255,120,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, s, s);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
